<template>
  <Dialog
    md
    title="Edit Shipping Address"
    show-close-button
    show-footer
    show-cancel-button
    show-save-button
    @close-event="close"
    @cancel-event="close"
    @save-event="saveAddress"
    :save-button-disabled="!isAddressChanged"
  >
    <template #modal-content>
      <form @submit.prevent="saveAddress" class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <!-- Name -->
          <div>
            <label class="block text-sm font-medium">Name</label>
            <input v-model="form.name" type="text" class="w-full border rounded px-3 py-2" />
            <p v-if="errors.name" class="text-red-500 text-xs">{{ errors.name }}</p>
          </div>

          <!-- Address line 1 -->
          <div>
            <label class="block text-sm font-medium">Address line 1</label>
            <input v-model="form.address1" type="text" class="w-full border rounded px-3 py-2" />
            <p v-if="errors.address1" class="text-red-500 text-xs">{{ errors.address1 }}</p>
          </div>

          <!-- Address line 2 -->
          <div>
            <label class="block text-sm font-medium">Address line 2</label>
            <input v-model="form.address2" type="text" class="w-full border rounded px-3 py-2" />
          </div>

          <!-- Postal code -->
          <div>
            <label class="block text-sm font-medium">Postal code</label>
            <input v-model="form.postalCode" type="text" class="w-full border rounded px-3 py-2" />
            <p v-if="errors.postalCode" class="text-red-500 text-xs">{{ errors.postalCode }}</p>
          </div>

          <!-- City -->
          <div>
            <label class="block text-sm font-medium">City</label>
            <input v-model="form.city" type="text" class="w-full border rounded px-3 py-2" />
            <p v-if="errors.city" class="text-red-500 text-xs">{{ errors.city }}</p>
          </div>
          
          <!-- City -->
          <div>
            <label class="block text-sm font-medium">State</label>
            <input v-model="form.state" type="text" class="w-full border rounded px-3 py-2" />
            <p v-if="errors.state" class="text-red-500 text-xs">{{ errors.state }}</p>
          </div>

          <!-- Country -->
          <div>
            <label class="block text-sm font-medium">Country</label>
            <Select
              :options="formattedCountries"
              v-model="form.country"
              type="text"
              append-to-body
              :class="{
                'border-red-500': errors.country,
              }"
              track-by="country_id"
              label="name"
              :reduce="(country) => country.name"
              placeholder="Select country"
            />
            <p v-if="errors.country" class="text-red-500 text-xs">{{ errors.country }}</p>
          </div>
        </div>
      </form>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from '@/components/Shared/Dialog.vue';
import Select from '@/components/Shared/Select.vue';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { Country } from '@/models';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import * as yup from 'yup';

const props = defineProps<{
  modelValue: boolean;
  initialAddress: {
    name: string;
    address1: string;
    address2?: string;
    postalCode: string;
    city: string;
    state : string;
    country: string;
  };
}>();
const countries = ref<Country[]>([]);
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save', value: typeof props.initialAddress): void;
}>();

// Form state
const form = reactive({ ...props.initialAddress });
const errors = reactive<{ [key: string]: string }>({});

// Reset form whenever modal opens
const close = () => {
  emit('close');
};
onMounted(()=>{
  if(!countries.value.length){
    getCountries();
  }
})
watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      Object.assign(form, props.initialAddress);
      Object.keys(errors).forEach((k) => (errors[k] = ''));
    }
  }
);

// Yup schema
const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  address1: yup.string().required('Address line 1 is required'),
  postalCode: yup.string().required('Postal code is required'),
  city: yup.string().required('City is required'),
  country: yup.string().required('Country is required'),
});

const isAddressChanged = computed(() => {
  return JSON.stringify(props.initialAddress) !== JSON.stringify(form);
});

// Save
const saveAddress = async () => {
  try {
    await schema.validate(form, { abortEarly: false });
    Object.keys(errors).forEach((key) => (errors[key] = ''));
    emit('save', { ...form });
    emit('close');
  } catch (err: any) {
    if (err.inner) {
      err.inner.forEach((e: any) => {
        errors[e.path] = e.message;
      });
    }
  }
};

const getCountries = async (): Promise<void> => {
  try {
    const response = await http.get<{ result: Country[] }>('/addresses/countries');
    countries.value = response.data.result;
  } catch (error: any) {
    handleResponseException(error);
  }
};

const formattedCountries = computed(() => {
  return countries.value.map((country) => ({
    country_id: country.id,
    name: country.name,
  }));
});
</script>
